import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';
import { fixMojibakeText } from '../utils/textEncoding';

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function localDateKey(value) {
  if (!value) return 'sin-fecha';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'sin-fecha';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch {
    return 'sin-fecha';
  }
}

function dayGroupLabel(key) {
  if (key === 'sin-fecha') return 'Sin fecha';
  if (key === todayISO()) return 'Hoy';
  if (key === daysAgoISO(1)) return 'Ayer';
  try {
    const [y, m, d] = key.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('es-CO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return key;
  }
}

function chipLabel(key) {
  if (key === 'sin-fecha') return '—';
  if (key === todayISO()) return 'Hoy';
  if (key === daysAgoISO(1)) return 'Ayer';
  try {
    const [y, m, d] = key.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
  } catch {
    return key;
  }
}

function formatHora(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function groupVisitasByDay(rows) {
  const groups = [];
  let current = null;
  for (const v of rows) {
    const key = localDateKey(v.inicio);
    if (!current || current.key !== key) {
      current = {
        key,
        label: dayGroupLabel(key),
        chip: chipLabel(key),
        items: [],
      };
      groups.push(current);
    }
    current.items.push(v);
  }
  return groups;
}

const RANGOS = [
  { id: 'hoy', label: 'Hoy', desde: () => todayISO(), hasta: () => todayISO() },
  { id: '7', label: '7 días', desde: () => daysAgoISO(7), hasta: () => todayISO() },
  { id: '30', label: '30 días', desde: () => daysAgoISO(30), hasta: () => todayISO() },
  { id: 'todo', label: 'Todo', desde: () => '', hasta: () => '' },
];

export default function HistorialAtenciones({ onToast, refreshKey = 0 }) {
  const [rango, setRango] = useState('7');
  const [desde, setDesde] = useState(() => daysAgoISO(7));
  const [hasta, setHasta] = useState(() => todayISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const listRef = useRef(null);

  const groups = useMemo(() => groupVisitasByDay(rows), [rows]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const desdeVal = desde || null;
    const hastaVal = hasta || null;
    api
      .getMisVisitas({
        limit: 120,
        ...(desdeVal ? { desde: desdeVal } : {}),
        ...(hastaVal ? { hasta: hastaVal } : {}),
      })
      .then((res) => {
        setRows(res.data || []);
      })
      .catch((err) => {
        setRows([]);
        setError(err.message || 'No se pudo cargar el historial');
        onToast?.(err.message || 'No se pudo cargar el historial', 'error');
      })
      .finally(() => setLoading(false));
  }, [desde, hasta, onToast]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  function applyRango(r) {
    setRango(r.id);
    setDesde(r.desde() || '');
    setHasta(r.hasta() || '');
  }

  function onDesdeChange(value) {
    setRango('custom');
    setDesde(value);
    if (hasta && value && value > hasta) setHasta(value);
  }

  function onHastaChange(value) {
    setRango('custom');
    setHasta(value);
    if (desde && value && value < desde) setDesde(value);
  }

  function scrollToDay(key) {
    const el = listRef.current?.querySelector(`[data-day="${key}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <section className="page historial-atenciones-page">
      <div className="page-header">
        <div>
          <h1>Historial</h1>
          <p className="page-subtitle">Clientes que ya visitaste</p>
        </div>
        <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
          Actualizar
        </button>
      </div>

      <div className="historial-rangos" role="tablist" aria-label="Rango de fechas">
        {RANGOS.map((r) => (
          <button
            key={r.id}
            type="button"
            role="tab"
            aria-selected={rango === r.id}
            className={`historial-rango-btn ${rango === r.id ? 'active' : ''}`}
            onClick={() => applyRango(r)}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="historial-date-filter" aria-label="Filtro por calendario">
        <label className="historial-date-field">
          <span>Desde</span>
          <input
            type="date"
            value={desde}
            max={hasta || undefined}
            onChange={(e) => onDesdeChange(e.target.value)}
          />
        </label>
        <label className="historial-date-field">
          <span>Hasta</span>
          <input
            type="date"
            value={hasta}
            min={desde || undefined}
            onChange={(e) => onHastaChange(e.target.value)}
          />
        </label>
        {desde && hasta && desde === hasta ? (
          <span className="historial-date-hint">Un día</span>
        ) : null}
      </div>

      {!loading && groups.length > 1 ? (
        <div className="historial-day-chips" aria-label="Ir a un día">
          {groups.map((g) => (
            <button
              key={g.key}
              type="button"
              className="historial-day-chip"
              onClick={() => scrollToDay(g.key)}
            >
              {g.chip}
            </button>
          ))}
        </div>
      ) : null}

      {error ? (
        <p className="error banner">
          {error}{' '}
          <button type="button" className="link-btn" onClick={load}>
            Reintentar
          </button>
        </p>
      ) : null}

      {loading ? <p className="muted">Cargando historial…</p> : null}

      {!loading && !rows.length ? (
        <div className="mis-clientes-empty">
          <p>Aún no hay atenciones en este rango.</p>
          <p className="muted">Cuando elijas un cliente en Mis clientes, aparecerá aquí.</p>
        </div>
      ) : null}

      {!loading && groups.length ? (
        <div className="historial-list" ref={listRef}>
          {groups.map((group) => (
            <section key={group.key} className="historial-day-group" data-day={group.key}>
              <header className="historial-day-header">
                <h2>{group.label}</h2>
                <span>
                  {group.items.length} visita{group.items.length === 1 ? '' : 's'}
                </span>
              </header>
              <div className="historial-day-cards">
                {group.items.map((v) => {
                  const nombre = fixMojibakeText(v.sucursal_nombre) || 'Cliente';
                  const codigo = v.sucursal_codigo || '—';
                  const ciudad = fixMojibakeText(v.ciudad);
                  const abierta = !v.fin;
                  const hora = formatHora(v.inicio);
                  return (
                    <article key={v.id} className={`historial-card ${abierta ? 'abierta' : ''}`}>
                      <div className="historial-card-main">
                        <span className="historial-card-code">
                          {v.es_sede || v.padre_codigo
                            ? `Cliente ${v.padre_codigo || '—'}`
                            : `Cód. ${codigo}`}
                        </span>
                        {v.es_sede || v.padre_codigo ? (
                          <span className="cliente-sede-badge">Sucursal</span>
                        ) : null}
                        <strong>
                          {v.es_sede || v.padre_codigo
                            ? fixMojibakeText(v.sede_etiqueta || v.display_nombre) || nombre
                            : nombre}
                        </strong>
                        {ciudad ? <span className="historial-card-meta">{ciudad}</span> : null}
                        {v.pedido_numero ? (
                          <span className="historial-card-meta">Pedido {v.pedido_numero}</span>
                        ) : null}
                      </div>
                      <div className="historial-card-side">
                        {hora ? <span className="historial-card-fecha">{hora}</span> : null}
                        {abierta ? <span className="historial-badge open">En curso</span> : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      ) : null}
    </section>
  );
}
